import { useCallback, useEffect, useState } from "react";
import { Pencil, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { MessageResponse } from "@/components/ai-elements/message";
import { AdminAlert, AdminEmptyState } from "@/components/shell/AdminPage";
import { SeedForm } from "@/components/kb/SeedForm";
import { useHermes } from "@/components/hermes/HermesProvider";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiError } from "@/lib/api";
import {
  createKbApi,
  findSeedPageDef,
  type KbPage,
  type KbWiki,
} from "@/lib/kb-api";
import { KB_COPY } from "@/lib/kb-copy";
import { isPageEditorDirty } from "@/lib/kb-page-editor-dirty";
import {
  readKbPageEditorMode,
  writeKbPageEditorMode,
} from "@/lib/kb-page-editor-mode";
import { isValidPageSlug, normalizePageSlug } from "@/lib/kb-page-slug";
import { seedFieldAnomaliesFromFrontmatter } from "@/lib/kb-page-status";
import {
  HERMES_WRITE_ACCEPTED_EVENT,
  shouldRefreshOpenPageEditor,
  type HermesWriteAcceptedDetail,
  type KnowledgeEntryFieldMeta,
} from "@/lib/hermes-knowledge-entry";
import { useOptionalAccountWorkspace } from "@/lib/account-workspace";
import { cn } from "@/lib/utils";

type PageEditorProps = {
  accountId: string;
  wiki: KbWiki | null;
  page: KbPage | null;
  canWrite: boolean;
  onSaved: (page: KbPage) => void;
};

export function PageEditor({ accountId, wiki, page, canWrite, onSaved }: PageEditorProps) {
  const kb = createKbApi(accountId);
  const workspace = useOptionalAccountWorkspace();
  const { startKnowledgeEntrySession } = useHermes();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [structured, setStructured] = useState<Record<string, unknown>>({});
  /** 编辑/预览跨条目保持（sessionStorage）；换 page.id 不重置 */
  const [preview, setPreview] = useState(() => readKbPageEditorMode() === "preview");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [slugDialogOpen, setSlugDialogOpen] = useState(false);
  const [slugDraft, setSlugDraft] = useState("");
  const [slugError, setSlugError] = useState<string | null>(null);
  const [isSavingSlug, setIsSavingSlug] = useState(false);

  const togglePreview = useCallback(() => {
    setPreview((value) => {
      const next = !value;
      writeKbPageEditorMode(next ? "preview" : "edit");
      return next;
    });
  }, []);

  useEffect(() => {
    if (!page) {
      setTitle("");
      setBody("");
      setStructured({});
      setError(null);
      return;
    }
    setTitle(page.title);
    setBody(page.body ?? "");
    setStructured(page.structured ?? {});
    setError(null);
  }, [page?.id]);

  // Hermes 来源深链：#seed-field-<属性键> → 滚动到种子表单字段
  useEffect(() => {
    if (!page?.is_seed) return;

    const scrollToSeedField = () => {
      const hash = window.location.hash;
      const match = hash.match(/^#seed-field-([a-zA-Z0-9_]+)$/);
      if (!match) return;
      const el = document.getElementById(`seed-field-${match[1]}`);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      if (typeof el.focus === "function") {
        window.setTimeout(() => el.focus(), 200);
      }
    };

    // 等表单字段挂载后再滚
    const timer = window.setTimeout(scrollToSeedField, 80);
    window.addEventListener("hashchange", scrollToSeedField);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("hashchange", scrollToSeedField);
    };
  }, [page?.id, page?.is_seed]);

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<HermesWriteAcceptedDetail>).detail;
      if (!detail) return;

      if (detail.action === "create_brand") {
        void workspace?.refreshBrands();
        return;
      }

      // page_upsert / seed_update：仅当前打开页拉取最新并同步本地 state
      // （父级 page prop 按 id 挂载，同 id 内容变更不会自动灌入 title/body）
      if (!page || !shouldRefreshOpenPageEditor(detail, page.id)) return;

      void kb
        .getPage(page.id)
        .then((result) => {
          onSaved(result.page);
          setStructured(result.page.structured ?? {});
          setBody(result.page.body ?? "");
          setTitle(result.page.title);
        })
        .catch(() => {
          toast.error("写入成功，但刷新页面失败，请手动刷新");
        });
    };

    window.addEventListener(HERMES_WRITE_ACCEPTED_EVENT, handler);
    return () => window.removeEventListener(HERMES_WRITE_ACCEPTED_EVENT, handler);
  }, [page, kb, onSaved, workspace]);

  type SaveOverrides = Partial<{
    structured: Record<string, unknown>;
    body: string;
    title: string;
  }>;

  const save = useCallback(
    async (overrides?: SaveOverrides): Promise<boolean> => {
      if (!page || !canWrite || isSaving) return false;
      setIsSaving(true);
      setError(null);
      try {
        const payload = page.is_seed
          ? {
              structured: overrides?.structured ?? structured,
              body: overrides?.body ?? body,
            }
          : {
              title: overrides?.title ?? title,
              body: overrides?.body ?? body,
            };
        const result = await kb.updatePage(page.id, payload);
        // 与服务端响应对齐，避免规范化后仍判定为 dirty
        setTitle(result.page.title);
        setBody(result.page.body ?? "");
        setStructured(result.page.structured ?? {});
        onSaved(result.page);
        toast.success("已保存");
        return true;
      } catch (caught) {
        const message = caught instanceof ApiError ? caught.message : "保存失败";
        setError(message);
        toast.error(message);
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [page, canWrite, isSaving, structured, body, title, kb, onSaved],
  );

  const openKnowledgeEntry = useCallback(() => {
    if (!page?.is_seed || !wiki || !canWrite) return;
    const seedDef = findSeedPageDef(wiki, page);
    const properties = seedDef?.properties ?? {};
    const fields: KnowledgeEntryFieldMeta[] = Object.entries(properties).map(([key, schema]) => ({
      key,
      label: schema.description ?? key,
    }));

    startKnowledgeEntrySession({
      mode: "knowledge_entry",
      accountId,
      pageId: page.id,
      wikiId: wiki.id,
      pageSlug: page.slug,
      brandId: wiki.brand_id ? String(wiki.brand_id) : null,
      structured,
      fields,
    });
  }, [page, wiki, canWrite, accountId, structured, startKnowledgeEntrySession]);

  const openSlugDialog = useCallback(() => {
    if (!page || page.is_seed || !canWrite) return;
    setSlugDraft(page.slug);
    setSlugError(null);
    setSlugDialogOpen(true);
  }, [page, canWrite]);

  const saveSlug = useCallback(async () => {
    if (!page || page.is_seed || !canWrite || isSavingSlug) return;
    const next = normalizePageSlug(slugDraft);
    if (!next || !isValidPageSlug(next)) {
      setSlugError(KB_COPY.editSlugInvalid);
      return;
    }
    if (next === page.slug) {
      setSlugDialogOpen(false);
      return;
    }

    setIsSavingSlug(true);
    setSlugError(null);
    try {
      const result = await kb.updatePage(page.id, { slug: next });
      onSaved(result.page);
      setSlugDialogOpen(false);
      toast.success(KB_COPY.editSlugSuccess);
    } catch (caught) {
      const message = caught instanceof ApiError ? caught.message : "保存失败";
      setSlugError(message);
      toast.error(message);
    } finally {
      setIsSavingSlug(false);
    }
  }, [page, canWrite, isSavingSlug, slugDraft, kb, onSaved]);

  const copySlugRef = useCallback(async () => {
    if (!page) return;
    const slugRef = `[[${page.slug}]]`;
    try {
      await navigator.clipboard.writeText(slugRef);
      toast.success(`${KB_COPY.copySlugRefSuccess}：${slugRef}`);
    } catch {
      toast.error("复制失败，请手动选择复制");
    }
  }, [page]);

  if (!wiki || !page) {
    return (
      <AdminEmptyState>{KB_COPY.editorEmpty}</AdminEmptyState>
    );
  }

  const seedDef = page.is_seed ? findSeedPageDef(wiki, page) : undefined;
  const dirty = isPageEditorDirty(page, { title, body, structured });
  const canEditSlug = canWrite && !page.is_seed;
  const slugRef = `[[${page.slug}]]`;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border px-4 py-3">
        <div className="min-w-0">
          <h2 className="truncate font-medium">{page.title}</h2>
          <div className="group inline-flex w-max max-w-full items-center gap-1">
            <button
              type="button"
              className="min-w-0 cursor-pointer truncate text-left text-xs text-muted-foreground hover:text-foreground"
              title={`${KB_COPY.copySlugRefHint} ${slugRef}`}
              aria-label={`${KB_COPY.copySlugRefHint} ${slugRef}`}
              onClick={() => void copySlugRef()}
            >
              {page.slug}
            </button>
            {canEditSlug ? (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-6 shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100"
                onClick={openSlugDialog}
                aria-label={KB_COPY.editSlugTitle}
              >
                <Pencil className="size-3" />
              </Button>
            ) : null}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {!page.is_seed ? (
            <Button type="button" variant="outline" size="sm" onClick={togglePreview}>
              {preview ? "编辑" : "预览"}
            </Button>
          ) : null}
          {page.is_seed && canWrite ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={openKnowledgeEntry}
            >
              <Sparkles className="size-4" />
              AI 录入
            </Button>
          ) : null}
          {canWrite ? (
            <Button
              type="button"
              size="sm"
              variant={dirty ? "default" : "outline"}
              onClick={() => void save()}
              disabled={!dirty || isSaving}
            >
              {isSaving ? "保存中…" : "保存"}
            </Button>
          ) : null}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        {error ? (
          <div className="mb-4">
            <AdminAlert>{error}</AdminAlert>
          </div>
        ) : null}

        {page.is_seed ? (
          <SeedForm
            seedDef={seedDef}
            structured={structured}
            body={body}
            disabled={!canWrite}
            pageId={page.id}
            fieldAnomalies={seedFieldAnomaliesFromFrontmatter(page.frontmatter)}
            onStructuredChange={setStructured}
            onBodyChange={setBody}
          />
        ) : preview ? (
          <MarkdownPreview body={body} />
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="page-title">{KB_COPY.freeTitleLabel}</Label>
              <Input
                id="page-title"
                value={title}
                disabled={!canWrite}
                placeholder={KB_COPY.freeTitlePlaceholder}
                onChange={(event) => setTitle(event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="page-body">{KB_COPY.freeBodyLabel}</Label>
              <p className="text-xs text-muted-foreground">{KB_COPY.freeBodyHint}</p>
              <textarea
                id="page-body"
                value={body}
                disabled={!canWrite}
                placeholder={KB_COPY.freeBodyPlaceholder}
                onChange={(event) => setBody(event.target.value)}
                rows={16}
                className={cn(
                  "border-input placeholder:text-muted-foreground dark:bg-input/30 flex min-h-[240px] w-full rounded-md border bg-transparent px-3 py-2 text-sm shadow-xs outline-none",
                  "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
                  "disabled:cursor-not-allowed disabled:opacity-50",
                )}
              />
            </div>
          </div>
        )}
      </div>

      {canEditSlug ? (
        <Dialog open={slugDialogOpen} onOpenChange={setSlugDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{KB_COPY.editSlugTitle}</DialogTitle>
              <DialogDescription>{KB_COPY.editSlugDescription}</DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Label htmlFor="page-slug-edit">{KB_COPY.editSlugLabel}</Label>
              <Input
                id="page-slug-edit"
                value={slugDraft}
                placeholder={KB_COPY.editSlugPlaceholder}
                onChange={(event) => {
                  setSlugDraft(event.target.value);
                  setSlugError(null);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    void saveSlug();
                  }
                }}
                autoComplete="off"
                spellCheck={false}
              />
              {slugError ? <p className="text-xs text-destructive">{slugError}</p> : null}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setSlugDialogOpen(false)}>
                取消
              </Button>
              <Button type="button" onClick={() => void saveSlug()} disabled={isSavingSlug}>
                {isSavingSlug ? "保存中…" : "保存"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  );
}

function MarkdownPreview({ body }: { body: string }) {
  if (!body.trim()) {
    return <p className="text-sm text-muted-foreground">暂无正文</p>;
  }

  return (
    <MessageResponse
      className={cn(
        "max-w-none text-sm leading-relaxed text-foreground",
        "[&>*:first-child]:mt-0 [&>*:last-child]:mb-0",
        "[&_h1]:mb-4 [&_h1]:mt-0 [&_h1]:text-2xl [&_h1]:font-semibold [&_h1]:tracking-tight",
        "[&_h2]:mb-3 [&_h2]:mt-6 [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:tracking-tight",
        "[&_h3]:mb-2 [&_h3]:mt-5 [&_h3]:text-lg [&_h3]:font-medium",
        "[&_h4]:mb-2 [&_h4]:mt-4 [&_h4]:text-base [&_h4]:font-medium",
        "[&_p]:my-3 [&_ul]:my-3 [&_ol]:my-3 [&_li]:my-1",
        "[&_blockquote]:my-3 [&_blockquote]:border-l-2 [&_blockquote]:border-border [&_blockquote]:pl-4 [&_blockquote]:text-muted-foreground",
        "[&_pre]:my-4 [&_code]:text-[0.9em] [&_table]:my-4 [&_table]:text-sm",
        "[&_a]:text-primary [&_a]:underline-offset-4 hover:[&_a]:underline",
      )}
      mode="static"
    >
      {body}
    </MessageResponse>
  );
}
